import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Form, Button, Container, Card, Alert, Table, Spinner, ProgressBar } from "react-bootstrap";
import axios from "axios";
import { getMockInstallmentData } from "./mockFleet";

const FleetVehicleForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const STATUS_OPTIONS = [
    { value: "active", label: t("fleet.statusActive") },
    { value: "inactive", label: t("fleet.statusInactive") },
    { value: "maintenance", label: t("fleet.statusMaintenance") },
    { value: "under_payment", label: t("fleet.statusUnderPayment") }
  ];

  const [formData, setFormData] = useState({
    plate: "",
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    color: "",
    vin: "",
    status: "active",
    driver_id: "",
    notes: "",
    insuranceEndDate: "",
    toEndDate: ""
  });
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [installmentInfo, setInstallmentInfo] = useState(null);
  const [installmentId, setInstallmentId] = useState(null);
  const [newPayment, setNewPayment] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    note: ""
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [ownerHistory, setOwnerHistory] = useState([]);
  const [ownerHistorySubmitting, setOwnerHistorySubmitting] = useState(false);
  const [newOwnerEntry, setNewOwnerEntry] = useState({
    driver_id: "",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: "",
    note: ""
  });

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  useEffect(() => {
    axios.get("/api/users", authHeaders()).then((res) => setDrivers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      setInstallmentInfo(null);
      setOwnerHistory([]);
      return;
    }
    setLoading(true);
    axios
      .get(`/api/fleet/vehicles/${id}`, authHeaders())
      .then((res) => {
        const v = res.data;
        setFormData({
          plate: v.plate || "",
          brand: v.brand || "",
          model: v.model || "",
          year: v.year || new Date().getFullYear(),
          color: v.color || "",
          vin: v.vin || "",
          status: v.status || "active",
          driver_id: v.driver_id != null ? String(v.driver_id) : "",
          notes: v.notes || "",
          insuranceEndDate: v.insuranceEndDate || "",
          toEndDate: v.toEndDate || ""
        });
        if (v.status === "under_payment" && (v.installmentData || v.installmentTransactions)) {
          const data = v.installmentData || getMockInstallmentData(v);
          const transactions = v.installmentTransactions || [];
          setInstallmentInfo({ installmentData: data, installmentTransactions: transactions });
          if (data && data.id) setInstallmentId(data.id);
        } else {
          setInstallmentInfo(null);
          setInstallmentId(null);
        }
        setOwnerHistory(Array.isArray(v.ownerHistory) ? v.ownerHistory : []);
      })
      .catch(() => setMessage(t("fleet.notFound")))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = value;
    setFormData((prev) => ({ ...prev, [name]: name === "year" ? parseInt(next, 10) || "" : next }));
    if (name === "status" && value !== "under_payment") setInstallmentInfo(null);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const driverOptions = drivers
    .map((u) => ({ id: u.id, name: [u.firstName, u.lastName].filter(Boolean).join(" ").trim() }))
    .filter((u) => u.name);

  const formatDateDisplay = (iso) => {
    if (!iso) return "–";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };

  const totalDebtToPay = () => {
    if (!installmentInfo) return 0;
    const { installmentData } = installmentInfo;
    return Math.max(0, Number(installmentData?.totalPrice || 0) - Number(installmentData?.initialDeposit || 0));
  };

  const totalPaid = () => {
    if (!installmentInfo) return 0;
    return (installmentInfo.installmentTransactions || []).reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
  };

  const currentRemainingDebt = () => Math.max(0, totalDebtToPay() - totalPaid());

  const paymentProgressPercent = () => {
    const debt = totalDebtToPay();
    if (debt <= 0) return 100;
    return Math.min(100, Math.round((totalPaid() / debt) * 100));
  };

  const handleAddPayment = (e) => {
    e.preventDefault();
    const amount = Number(newPayment.amount);
    const instId = installmentInfo?.installmentData?.id ?? installmentId;
    if (!amount || amount <= 0 || !instId) return;
    setPaymentSubmitting(true);
    axios
      .post(
        `/api/fleet/installments/${instId}/transactions`,
        {
          date: newPayment.date,
          amount,
          note: newPayment.note.trim() || t("fleet.placeholderNote")
        },
        authHeaders()
      )
      .then((res) => {
        const tx = {
          id: res.data.id,
          date: newPayment.date,
          amount,
          type: "Rata",
          note: newPayment.note.trim() || t("fleet.placeholderNote"),
          balance: res.data.balance
        };
        setInstallmentInfo((prev) => ({
          ...prev,
          installmentTransactions: [tx, ...(prev.installmentTransactions || [])]
        }));
        setNewPayment({ date: new Date().toISOString().slice(0, 10), amount: "", note: "" });
      })
      .catch(() => {})
      .finally(() => setPaymentSubmitting(false));
  };

  const handleAddOwnerEntry = (e) => {
    e.preventDefault();
    const driverId = newOwnerEntry.driver_id ? Number(newOwnerEntry.driver_id) : null;
    if (!driverId || !newOwnerEntry.date_from || !id) return;
    setOwnerHistorySubmitting(true);
    axios
      .post(
        `/api/fleet/vehicles/${id}/owner-history`,
        {
          driver_id: driverId,
          date_from: newOwnerEntry.date_from,
          date_to: newOwnerEntry.date_to || undefined,
          note: newOwnerEntry.note || undefined
        },
        authHeaders()
      )
      .then((res) => {
        const entry = res.data.entry || {
          driver_id: driverId,
          driverName: driverOptions.find((d) => d.id === driverId)?.name || `#${driverId}`,
          date_from: newOwnerEntry.date_from,
          date_to: newOwnerEntry.date_to || null,
          note: newOwnerEntry.note || null
        };
        setOwnerHistory((prev) => [...prev, entry]);
        setNewOwnerEntry({
          driver_id: "",
          date_from: new Date().toISOString().slice(0, 10),
          date_to: "",
          note: ""
        });
      })
      .catch(() => {})
      .finally(() => setOwnerHistorySubmitting(false));
  };

  const validate = () => {
    const errs = {};
    const plate = String(formData.plate).trim();
    if (!plate) errs.plate = t("fleet.errorPlate");
    else if (plate.length > 20) errs.plate = t("fleet.errorPlateLong");
    if (!String(formData.brand).trim()) errs.brand = t("fleet.errorBrand");
    if (!String(formData.model).trim()) errs.model = t("fleet.errorModel");
    const y = formData.year;
    const maxYear = new Date().getFullYear() + 1;
    if (!y || y < 1990 || y > maxYear) errs.year = t("fleet.errorYear", { max: maxYear });
    const vin = String(formData.vin).trim();
    if (vin && vin.length !== 17) errs.vin = t("fleet.errorVin");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    if (!validate()) return;
    const payload = {
      plate: formData.plate.trim(),
      brand: formData.brand.trim(),
      model: formData.model.trim(),
      year: formData.year,
      color: formData.color || null,
      vin: formData.vin || null,
      status: formData.status,
      driver_id: formData.driver_id ? Number(formData.driver_id) : null,
      notes: formData.notes || null,
      insuranceEndDate: formData.insuranceEndDate || null,
      toEndDate: formData.toEndDate || null
    };
    const promise = isEdit
      ? axios.put(`/api/fleet/vehicles/${id}`, payload, authHeaders())
      : axios.post("/api/fleet/vehicles", payload, authHeaders());
    promise
      .then(() => {
        setMessage(isEdit ? t("fleet.changesSaved") : t("fleet.vehicleAdded"));
        if (isEdit) {
          axios.get(`/api/fleet/vehicles/${id}`, authHeaders()).then((r) => {
            if (Array.isArray(r.data?.ownerHistory)) setOwnerHistory(r.data.ownerHistory);
          }).catch(() => {});
        }
        setTimeout(() => navigate("/admin/fleet"), 1500);
      })
      .catch((err) => setMessage(err.response?.data?.error || err.message || t("fleet.saveError")));
  };

  if (loading) {
    return (
      <Container className="mt-4 d-flex align-items-center justify-content-center" style={{ minHeight: "200px" }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header as="h5">
          {isEdit ? t("fleet.editVehicleTitle") : t("fleet.addVehicleTitle")}
        </Card.Header>
        <Card.Body>
          {message && <Alert variant={message === t("fleet.changesSaved") || message === t("fleet.vehicleAdded") ? "success" : "danger"} dismissible onClose={() => setMessage("")}>{message}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.plateRequired")}</Form.Label>
              <Form.Control
                name="plate"
                value={formData.plate}
                onChange={handleChange}
                placeholder={t("fleet.placeholderPlate")}
                isInvalid={!!errors.plate}
              />
              <Form.Control.Feedback type="invalid">{errors.plate}</Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.brand")} *</Form.Label>
                  <Form.Control
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder={t("fleet.placeholderBrand")}
                    isInvalid={!!errors.brand}
                  />
                  <Form.Control.Feedback type="invalid">{errors.brand}</Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.model")} *</Form.Label>
                  <Form.Control
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder={t("fleet.placeholderModel")}
                    isInvalid={!!errors.model}
                  />
                  <Form.Control.Feedback type="invalid">{errors.model}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.year")} *</Form.Label>
                  <Form.Control
                    type="number"
                    name="year"
                    value={formData.year || ""}
                    onChange={handleChange}
                    min={1990}
                    max={new Date().getFullYear() + 1}
                    isInvalid={!!errors.year}
                  />
                  <Form.Control.Feedback type="invalid">{errors.year}</Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.color")}</Form.Label>
                  <Form.Control
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder={t("fleet.placeholderColor")}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("vehicleStatus")}</Form.Label>
                  <Form.Select name="status" value={formData.status} onChange={handleChange}>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.vin")}</Form.Label>
              <Form.Control
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                placeholder={t("fleet.vinPlaceholder")}
                maxLength={17}
                isInvalid={!!errors.vin}
              />
              <Form.Control.Feedback type="invalid">{errors.vin}</Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.insuranceEnd")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="insuranceEndDate"
                    value={formData.insuranceEndDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("fleet.toEnd")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="toEndDate"
                    value={formData.toEndDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{t("fleet.driver")}</Form.Label>
              <Form.Select
                name="driver_id"
                value={formData.driver_id}
                onChange={handleChange}
                disabled={formData.status === "under_payment"}
              >
                <option value="">{t("fleet.noDriver")}</option>
                {driverOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Form.Select>
              {formData.status === "under_payment" && (
                <Form.Text className="text-muted">
                  {t("fleet.driverNoChange")}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t("fleet.notes")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder={t("fleet.placeholderNotes")}
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button type="submit" variant="primary">
                {isEdit ? t("save") : t("add")}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => navigate("/admin/fleet")}>
                {t("cancel")}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {isEdit && (
        <Card className="mt-4">
          <Card.Header as="h6" className="bg-light">{t("fleet.ownerHistoryTitle")}</Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddOwnerEntry} className="row g-2 align-items-end mb-3">
              <div className="col-md-3">
                <Form.Label className="small mb-0">{t("fleet.ownerHistoryDriver")}</Form.Label>
                <Form.Select
                  value={newOwnerEntry.driver_id}
                  onChange={(e) => setNewOwnerEntry((p) => ({ ...p, driver_id: e.target.value }))}
                  required
                >
                  <option value="">— {t("fleet.driver")} —</option>
                  {driverOptions.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Form.Label className="small mb-0">{t("fleet.ownerHistoryFrom")}</Form.Label>
                <Form.Control
                  type="date"
                  value={newOwnerEntry.date_from}
                  onChange={(e) => setNewOwnerEntry((p) => ({ ...p, date_from: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-2">
                <Form.Label className="small mb-0">{t("fleet.ownerHistoryTo")}</Form.Label>
                <Form.Control
                  type="date"
                  value={newOwnerEntry.date_to}
                  onChange={(e) => setNewOwnerEntry((p) => ({ ...p, date_to: e.target.value }))}
                />
              </div>
              <div className="col-md-3">
                <Form.Label className="small mb-0">{t("fleet.ownerHistoryNote")}</Form.Label>
                <Form.Control
                  type="text"
                  value={newOwnerEntry.note}
                  onChange={(e) => setNewOwnerEntry((p) => ({ ...p, note: e.target.value }))}
                  placeholder={t("fleet.notesPlaceholder")}
                />
              </div>
              <div className="col-md-2">
                <Button type="submit" variant="outline-primary" size="sm" disabled={ownerHistorySubmitting}>
                  {ownerHistorySubmitting ? "…" : t("fleet.ownerHistoryAdd")}
                </Button>
              </div>
            </Form>
            {ownerHistory.length === 0 ? (
              <p className="text-muted small mb-0">{t("fleet.ownerHistoryEmpty")}</p>
            ) : (
              <Table striped bordered hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("fleet.ownerHistoryFrom")}</th>
                    <th>{t("fleet.ownerHistoryTo")}</th>
                    <th>{t("fleet.ownerHistoryDriver")}</th>
                    <th>{t("fleet.ownerHistoryNote")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...ownerHistory].reverse().map((entry, idx) => {
                    const isCurrent = idx === ownerHistory.length - 1 && !entry.date_to;
                    return (
                      <tr key={idx} className={isCurrent ? "table-success" : undefined}>
                        <td>{formatDateDisplay(entry.date_from)}</td>
                        <td>
                          {isCurrent ? (
                            <span className="badge bg-success">{t("fleet.ownerHistoryCurrentlyUsing")}</span>
                          ) : entry.date_to ? (
                            formatDateDisplay(entry.date_to)
                          ) : (
                            "–"
                          )}
                        </td>
                        <td>{entry.driverName || `#${entry.driver_id}`}</td>
                        <td className="small">{entry.note || "–"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {formData.status === "under_payment" && installmentInfo && (
        <>
          <Card className="mt-4">
            <Card.Header as="h6" className="bg-light">{t("fleet.initialDataTitle")}</Card.Header>
            <Card.Body className="small">
              <div className="row">
                <div className="col-md-6">
                  <dl className="row mb-0">
                    <dt className="col-sm-5">{t("fleet.driver")}</dt>
                    <dd className="col-sm-7">{installmentInfo.installmentData.driverName || "–"}</dd>
                    <dt className="col-sm-5">{t("fleet.vehicleValue")}</dt>
                    <dd className="col-sm-7">{Number(installmentInfo.installmentData.totalPrice || 0).toLocaleString("pl-PL")} PLN</dd>
                    <dt className="col-sm-5">{t("fleet.initialDeposit")}</dt>
                    <dd className="col-sm-7">{Number(installmentInfo.installmentData.initialDeposit || 0).toLocaleString("pl-PL")} PLN</dd>
                  </dl>
                </div>
                <div className="col-md-6">
                  <dl className="row mb-0">
                    <dt className="col-sm-5">{t("fleet.pricePerWeek")}</dt>
                    <dd className="col-sm-7">{Number(installmentInfo.installmentData.pricePerWeek || 0).toLocaleString("pl-PL")} PLN</dd>
                    <dt className="col-sm-5">{t("fleet.weeksCount")}</dt>
                    <dd className="col-sm-7">{installmentInfo.installmentData.weeks || "–"}</dd>
                    <dt className="col-sm-5">{t("fleet.createdAt")}</dt>
                    <dd className="col-sm-7">{formatDateDisplay(installmentInfo.installmentData.createdAt)}</dd>
                  </dl>
                </div>
              </div>
            </Card.Body>
          </Card>
          <Card className="mt-3">
            <Card.Header as="h6" className="bg-light">{t("fleet.currentStatusTitle")}</Card.Header>
            <Card.Body className="small">
              <div className="mb-2">
                <strong>{t("fleet.paidTotal")}:</strong>{" "}
                <span className="text-success">{totalPaid().toLocaleString("pl-PL")} PLN</span>
                {" "}
                {t("fleet.ofToPay", { total: totalDebtToPay().toLocaleString("pl-PL") })}
              </div>
              <ProgressBar
                now={paymentProgressPercent()}
                label={`${paymentProgressPercent()}%`}
                variant={paymentProgressPercent() >= 100 ? "success" : "primary"}
                striped={paymentProgressPercent() < 100}
                animated={paymentProgressPercent() < 100}
              />
              <div className="mt-1 text-muted">
                {t("fleet.remainingToPay")}: <strong>{currentRemainingDebt().toLocaleString("pl-PL")} PLN</strong>
              </div>
            </Card.Body>
          </Card>
          <Card className="mt-3">
            <Card.Header as="h6" className="bg-light">{t("fleet.addRateManually")}</Card.Header>
            <Card.Body>
              <Form onSubmit={handleAddPayment} className="row g-2 align-items-end">
                <div className="col-md-2">
                  <Form.Label className="small mb-0">{t("fleet.date")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={newPayment.date}
                    onChange={(e) => setNewPayment((p) => ({ ...p, date: e.target.value }))}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <Form.Label className="small mb-0">{t("fleet.amountPln")}</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    step={1}
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
                    placeholder={t("fleet.placeholderAmount")}
                    required
                  />
                </div>
                <div className="col-md-5">
                  <Form.Label className="small mb-0">{t("fleet.notesPlaceholder")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={newPayment.note}
                    onChange={(e) => setNewPayment((p) => ({ ...p, note: e.target.value }))}
                    placeholder={t("fleet.placeholderNote")}
                  />
                </div>
                <div className="col-md-2">
                  <Button type="submit" variant="outline-primary" size="sm" disabled={paymentSubmitting}>
                    {paymentSubmitting ? "…" : t("fleet.addRate")}
                  </Button>
                </div>
              </Form>
              <div className="small text-muted mt-1">{t("fleet.remainingToPay")}: <strong>{currentRemainingDebt().toLocaleString("pl-PL")} PLN</strong></div>
            </Card.Body>
          </Card>
          <Card className="mt-3">
            <Card.Header as="h6" className="bg-light">{t("fleet.transactionHistory")}</Card.Header>
            <Card.Body className="p-0">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("fleet.date")}</th>
                    <th>{t("fleet.type")}</th>
                    <th className="text-end">{t("fleet.amountPln")}</th>
                    <th className="text-end">{t("fleet.remainingPln")}</th>
                    <th>{t("fleet.notesPlaceholder")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(installmentInfo.installmentTransactions || []).map((tx) => (
                    <tr key={tx.id}>
                      <td>{formatDateDisplay(tx.date)}</td>
                      <td>{tx.type || t("fleet.rate")}</td>
                      <td className="text-end">{Number(tx.amount).toLocaleString("pl-PL")}</td>
                      <td className="text-end">{tx.balance != null ? Number(tx.balance).toLocaleString("pl-PL") : "–"}</td>
                      <td className="small">{tx.note || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default FleetVehicleForm;
